/**
 * useLatestUsers Hook
 *
 * State orchestration for the admin Latest Users sheet: initial fetch,
 * infinite-scroll pagination with fetch guards, the order-by toggle that
 * resets and refetches, and unique-append dedup.
 *
 * Ports Flutter's `LatestUsersScreenState` (`_getData`, the eye-icon toggle,
 * and the post-frame initial load) from
 * `lib/presentation/screens/user/latest_users_screen.dart:30-142`.
 *
 * Business logic lives here, not in the owning sheet component.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { PageCursor } from '@/data/types/json';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { LastUsersOrderBy } from '@/domain/enums/user/lastUsersOrderBy';
import { createFetchLatestUsersUseCase } from '@/domain/usecases/user/fetchLatestUsersUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseLatestUsersResult {
  users: UserEntity[];
  hasMore: boolean;
  isFetching: boolean;
  /** True until the first fetch resolves; the sheet shows a centered loader. */
  isFirstLoad: boolean;
  orderBy: LastUsersOrderBy;
  loadMore: () => void;
  toggleOrderBy: () => void;
}

export function useLatestUsers(): UseLatestUsersResult {
  const [users, setUsers] = useState<UserEntity[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [orderBy, setOrderBy] = useState<LastUsersOrderBy>(
    LastUsersOrderBy.CREATED_TIMESTAMP
  );

  // Refs mirror the synchronous Flutter fields (`_lastFetchedUser`,
  // `_hasMore`, `_isFetching`, `_orderBy`). React state updates are async, so
  // the fetch reads the refs to make its guards and cursor handling reliable
  // across rapid pagination/toggle calls.
  const cursorRef = useRef<PageCursor | undefined>(undefined);
  const hasMoreRef = useRef(true);
  const isFetchingRef = useRef(false);
  const orderByRef = useRef<LastUsersOrderBy>(LastUsersOrderBy.CREATED_TIMESTAMP);

  // Ports Flutter `_getData` (latest_users_screen.dart:73-98).
  const loadMore = useCallback(async () => {
    // Early-return when `!_hasMore || _isFetching` (line 74).
    if (!hasMoreRef.current || isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setIsFetching(true);

    try {
      const result = await createFetchLatestUsersUseCase().execute({
        orderBy: orderByRef.current,
        cursor: cursorRef.current,
      });

      // Port `_users.addAllUnique(response.users)`
      // (list_extensions.dart:5-11). Dedup by `firebaseId` identity.
      // Flutter's `User ==` also compares `contentCount`
      // (user.dart:115-123), but its `hashCode` is `firebaseId.hashCode`
      // alone and every other web user-list flow dedups by id; for a
      // paginated newest-first list this is the intended behaviour, so the
      // `contentCount` tiebreak is deliberately dropped here.
      setUsers((prev) => {
        const existingIds = new Set(prev.map((u) => u.firebaseId));
        const additions = result.users.filter(
          (u) => !existingIds.has(u.firebaseId)
        );
        return additions.length > 0 ? [...prev, ...additions] : prev;
      });

      cursorRef.current = result.cursor;
      hasMoreRef.current = result.hasMore;
      setHasMore(result.hasMore);
    } catch (error) {
      appLogger.error('Error fetching latest users', 'useLatestUsers.loadMore', {
        error: error as Error,
      });
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
      setIsFirstLoad(false);
    }
  }, []);

  // Ports the eye-icon `onPressed` (latest_users_screen.dart:133-142): flip
  // `_orderBy`, reset `_lastFetchedUser = null`, `_hasMore = true`,
  // `_users.clear()`, then `await _getData()`.
  const toggleOrderBy = useCallback(() => {
    const next =
      orderByRef.current === LastUsersOrderBy.CREATED_TIMESTAMP
        ? LastUsersOrderBy.LAST_SEEN_TIMESTAMP
        : LastUsersOrderBy.CREATED_TIMESTAMP;

    orderByRef.current = next;
    cursorRef.current = undefined;
    hasMoreRef.current = true;
    // Clear the in-flight guard so the post-reset fetch always runs even if a
    // prior `loadMore` was mid-flight (its `finally` will still set the ref
    // back to false harmlessly after this fetch starts).
    isFetchingRef.current = false;

    setOrderBy(next);
    setHasMore(true);
    setUsers([]);

    void loadMore();
  }, [loadMore]);

  // Initial load once on mount, mirroring Flutter's post-frame `_getData`
  // (latest_users_screen.dart:42, 56-58).
  useEffect(() => {
    void loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    users,
    hasMore,
    isFetching,
    isFirstLoad,
    orderBy,
    loadMore,
    toggleOrderBy,
  };
}
